import { hasPlanAtLeast, type PlanLevels } from './plan-access';

export type AccessRule = {
  requiredPlan: string;
  requiredInterval: string;
  requiredTags?: string[] | null;
};

export type AccessSubject = {
  plan?: string | null;
  interval?: string | null;
  tags?: string[] | null;
};

export type AccessEvaluation = {
  allowed: boolean;
  hasPlanReq: boolean;
  hasTagReq: boolean;
  planAndIntOk: boolean;
  tagOk: boolean;
};

export function intervalMatch(required: string, userInterval: string | null | undefined): boolean {
  if (required === '*') return true;
  if (!userInterval) return false;
  return required === userInterval;
}

function tagsMatch(
  requiredTags: string[] | null | undefined,
  userTags: string[] | null | undefined,
): boolean {
  if (!requiredTags || requiredTags.length === 0) return false;
  if (!userTags || userTags.length === 0) return false;
  return requiredTags.some(t => userTags.includes(t));
}

/**
 * 评估模型访问权限。两个 gate 之间为 OR：满足任一已配置 gate 即可。
 *   - Plan gate: planOk AND intervalOk（plan 与 interval 同组，内部仍是 AND）
 *   - Tag  gate: user has any of requiredTags（数组内部 OR）
 *
 * 行为：
 *   - 两个 gate 都未配置 → allowed=true（无任何要求）。
 *   - 只配置一个 gate → 只看该 gate。
 *   - 两个 gate 都配置 → 满足任一即放行。
 *
 * 不处理 `enabled` 开关与 model pass 次卡通道，调用方在 evaluator 之外处理。
 */
export function evaluateModelAccess(
  rule: AccessRule,
  subject: AccessSubject,
  planLevels?: PlanLevels,
): AccessEvaluation {
  const planOk =
    rule.requiredPlan === '*' ||
    hasPlanAtLeast(subject.plan ?? '*', rule.requiredPlan, planLevels);
  const intOk = intervalMatch(rule.requiredInterval, subject.interval);
  const planAndIntOk = planOk && intOk;

  const hasPlanReq = rule.requiredPlan !== '*' || rule.requiredInterval !== '*';
  const hasTagReq = rule.requiredTags != null && rule.requiredTags.length > 0;
  const tagOk = hasTagReq && tagsMatch(rule.requiredTags, subject.tags);

  const allowed =
    (!hasPlanReq && !hasTagReq) ||
    (hasPlanReq && planAndIntOk) ||
    (hasTagReq && tagOk);

  return { allowed, hasPlanReq, hasTagReq, planAndIntOk, tagOk };
}

/** 仅判断 rule 是否完全无访问要求（既无 plan/interval 限制，也无 tag）。 */
export function hasAnyAccessRequirement(rule: Pick<AccessRule, 'requiredPlan' | 'requiredInterval' | 'requiredTags'>): boolean {
  const hasPlanReq = rule.requiredPlan !== '*' || rule.requiredInterval !== '*';
  const hasTagReq = rule.requiredTags != null && rule.requiredTags.length > 0;
  return hasPlanReq || hasTagReq;
}
